// EveryPlace Agent Onboarding — main wizard.
// Ported from the inline <script> block in the prototype `Agent Onboarding.html`.
// Design and behavior are preserved; data now comes from the API (Phase 4):
//   • agent      ← GET /v1/me (set once the email-verify session exists)
//   • listings   ← GET /v1/listings/mine
//   • escrows    ← GET /v1/escrows/mine (sell-side auto-detect via MLS index)
//   • bbas       ← GET /v1/bbas (FUB)
// localStorage is kept ONLY for current-step convenience; server state is the
// source of truth.
import React, { useState, useEffect, useRef, useMemo } from "react";

import {
  IcSearch, IcUser, IcDoc, IcBuilding, IcEscrow, IcHandshake, IcShield,
  IcCheck, IcLock, IcArrow, IcX, IcExternal, EPLogo,
  LISTINGS as MOCK_LISTINGS,
} from "./components/shared.jsx";
import { StepSearch } from "./components/step_search.jsx";
import { StepProfile, StepAgreements } from "./components/steps_early.jsx";
import { StepWelcomeForm } from "./components/step_welcome_form.jsx";
import { StepIntro } from "./components/step_intro.jsx";
import { StepListings, StepADRE } from "./components/steps_late.jsx";
import { StepAssociations } from "./components/step_associations.jsx";
import { StepEscrow } from "./components/step_escrow.jsx";
import { StepBBA } from "./components/step_bba.jsx";
import { StepFinish } from "./components/step_finish.jsx";
import { StepCircle } from "./components/step_circle.jsx";
import { SignatureModal } from "./components/signature.jsx";
import { useResource } from "./lib/useResource.js";
import { api } from "./lib/api.js";
import { resumeStepKey } from "./lib/resume-step.js";
import { stepEyebrow } from "./lib/step-number.js";
import { useIsMobile } from "./lib/useIsMobile.js";

const TWEAK_DEFAULTS = {
  layout: "wizard",
  signatureStyle: "docusign",
};

// Order matters only here — every gate resolves by KEY via stepIdx(), never by a
// literal index, so this array is the single place the sequence is decided.
const STEPS = [
  { k: "search",       label: "Find You",                icon: <IcSearch size={14}/>    },
  // Profile confirmation precedes the welcome video: the agent corrects their
  // details while they still have momentum from signing in, rather than after a
  // 53-second video. Swapped 2026-07-30.
  { k: "profile",      label: "Your Profile",            icon: <IcUser size={14}/>      },
  { k: "intro",        label: "Welcome to EveryPlace",   icon: <IcCheck size={14}/>     },
  { k: "agreements",   label: "Agreements",              icon: <IcDoc size={14}/>       },
  { k: "welcome",      label: "Welcome Form",            icon: <IcDoc size={14}/>       },
  { k: "adre",         label: "ADRE Transfer",           icon: <IcShield size={14}/>    },
  { k: "associations", label: "Association Transfers",   icon: <IcBuilding size={14}/>  },
  { k: "listings",     label: "Listing Transfers",       icon: <IcBuilding size={14}/>  },
  { k: "escrow",       label: "Escrow Transfers",        icon: <IcEscrow size={14}/>    },
  { k: "bba",          label: "Buyer Agreements",        icon: <IcHandshake size={14}/> },
  { k: "finish",       label: "Finish",                  icon: <IcCheck size={14}/>     },
  // Post-onboarding handoff: training lives in Circle, so this is the last thing
  // an agent sees. Kept OUT of the transfer-step skip filter — every agent,
  // recruit or transfer, needs Circle.
  { k: "circle",       label: "Circle Training",         icon: <IcExternal size={14}/>  },
];

// Placeholder identity for the wizard chrome before the agent verifies (step 0
// is the only thing shown then, and StepSearch doesn't use it).
const GUEST = { name: "Guest", email: "", phone: "", license: "", mlsId: "", shortId: "", currentBroker: "My Home Group LLC" };

export default function App() {
  // Tweaks state
  const [tweaks, setTweaks] = useState(() => {
    try { return { ...TWEAK_DEFAULTS, ...JSON.parse(localStorage.getItem("ep_onb_tweaks") || "{}") }; }
    catch { return TWEAK_DEFAULTS; }
  });
  const [tweaksOn, setTweaksOn] = useState(false);

  // Current signed-in agent (email-verify session). 401 => not signed in yet.
  // `viewAs` is carried alongside the agent so the read-only banner can render.
  // Only ever present in an Agent Services preview; see routes/staff-portal.js.
  const me = useResource("/v1/me", {
    pick: (r) => (r?.agent ? { ...r.agent, viewAs: r.viewAs || null } : r?.agent),
  });
  const authed = !!me.data;
  const [agentEdits, setAgentEdits] = useState({});
  const agent = { ...(me.data || GUEST), ...agentEdits };
  // Agent Services is previewing somebody. Changes two things: the wizard's
  // position comes from the server rather than this browser (see the placement
  // effect below), and nothing about the preview is persisted locally.
  const previewing = !!me.data?.viewAs?.readOnly;

  // Wizard step (persisted). Unauthed agents are pinned to step 0 (Find You).
  const [step, setStep] = useState(() => {
    const s = localStorage.getItem("ep_onb_step");
    return s ? parseInt(s, 10) : 0;
  });
  const [furthest, setFurthest] = useState(() => parseInt(localStorage.getItem("ep_onb_furthest") || "0", 10));
  // Did THIS browser already know where the agent was, at mount?
  //
  // Captured once, here, because the persist effect below writes ep_onb_step on
  // the first render — so by the time the resume effect runs, localStorage always
  // has a value and the question can no longer be asked.
  const [hadStoredStep] = useState(() => localStorage.getItem("ep_onb_step") !== null);
  const effectiveStep = authed ? Math.max(step, 1 > step ? 1 : step) : 0;

  const [agreements, setAgreements] = useState({});
  const [associations, setAssociations] = useState({ assoc: [], actions: {}, formStatus: {} });
  const [adreConfirmed, setAdreConfirmed] = useState(false);
  const [notionId, setNotionId] = useState(null);

  // Founding agent: anyone signing within 30 days of launch (2026-06-01)
  const isFoundingAgent = useMemo(
    () => (Date.now() - new Date("2026-06-01").getTime()) / 86400000 <= 30,
    []
  );

  // Onboarding gate (declared early — the resource hooks below depend on the
  // dynamic step list). See the comment at the goTo guard for semantics.
  const [gate, setGate] = useState(null);
  const refreshGate = async () => {
    try { const g = await api.get("/v1/onboarding-gate/status"); setGate(g); return g; }
    catch { return null; /* fail open — status route absent or transient error */ }
  };
  useEffect(() => { if (authed) refreshGate(); }, [authed]); // eslint-disable-line react-hooks/exhaustive-deps

  // The gate only opens when BoldSign's webhook reaches us, and that is NOT
  // prompt — a real ICA signing took FIVE MINUTES from send to
  // ica_completed_at. This used to be a single `setTimeout(refreshGate, 4000)`,
  // so it almost always ran long before the webhook landed and then never tried
  // again: the wizard sat showing an unsigned ICA until the agent reloaded by
  // hand, with nothing on screen suggesting a reload would help.
  //
  // Poll with backoff instead, and stop as soon as the server agrees. Total
  // window is ~8 minutes, comfortably past the 5 observed; if it does expire the
  // page-load refetch still recovers the true state.
  const gatePoll = useRef(null);
  const stopGatePoll = () => {
    if (gatePoll.current) { clearTimeout(gatePoll.current); gatePoll.current = null; }
  };
  const pollGateUntilSigned = () => {
    if (gatePoll.current) return; // never run two chains at once
    const delays = [2000, 3000, 5000, 8000, 12000, 20000, ...Array(14).fill(30000)];
    let i = 0;
    const tick = async () => {
      const g = await refreshGate();
      // !tracked = pre-pipeline agent, nothing to wait for.
      if (g && (g.icaDone || g.unlocked || !g.tracked)) { gatePoll.current = null; return; }
      if (i >= delays.length) { gatePoll.current = null; return; }
      gatePoll.current = setTimeout(tick, delays[i++]);
    };
    gatePoll.current = setTimeout(tick, delays[i++]);
  };
  useEffect(() => stopGatePoll, []); // clear the pending timer on unmount

  // Re-check whenever this tab regains focus. Signing happens in a SEPARATE
  // BoldSign tab, so "the user came back to us" is the one reliable signal that
  // something may have changed — and it is exactly the moment they expect to see
  // it reflected. The timed poll above covers roughly eight minutes from send;
  // this covers everything after that, including closing the laptop mid-signing
  // and returning the next day.
  //
  // Deliberately cheap: only fires when there is actually something outstanding,
  // so a signed-and-unlocked agent switching tabs costs nothing.
  useEffect(() => {
    if (!authed) return;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      // Also refreshes when unlocked but the mailbox details are still missing —
      // see the mailbox poll below for why those arrive after the unlock.
      if (gate && gate.tracked && (!gate.unlocked || !gate.ltEmail)) refreshGate();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [authed, gate?.tracked, gate?.unlocked, gate?.ltEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  // The mailbox details arrive AFTER the gate unlocks, so one more short poll.
  //
  // `unlocked` is set by the completion gate; step-2c then runs and gworkspace
  // writes the created address into its provisioning detail — 5.1 seconds later
  // on the 2026-08-12 run. The Circle step reads ltEmail/mailboxPassword from
  // that row, and pollGateUntilSigned stops the instant `unlocked` is true, so
  // the snapshot the page held said null forever and the agent was shown
  // "we're creating your address now" with no address and no password.
  //
  // Bounded on purpose: gworkspace can legitimately never produce an address
  // (guarded, unconfigured, or errored), and an unbounded poll would spin for
  // the rest of the session. ~70s comfortably covers a step-2c run that takes
  // about ten.
  const mailboxPoll = useRef(null);
  useEffect(() => {
    if (!authed || !gate?.tracked || !gate?.unlocked || gate?.ltEmail) return;
    if (mailboxPoll.current) return;
    const delays = [2000, 3000, 5000, 8000, 12000, 20000, 20000];
    let i = 0;
    const tick = async () => {
      const g = await refreshGate();
      if (g?.ltEmail || i >= delays.length) { mailboxPoll.current = null; return; }
      mailboxPoll.current = setTimeout(tick, delays[i++]);
    };
    mailboxPoll.current = setTimeout(tick, delays[i++]);
    return () => { if (mailboxPoll.current) { clearTimeout(mailboxPoll.current); mailboxPoll.current = null; } };
  }, [authed, gate?.tracked, gate?.unlocked, gate?.ltEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  // "No transactions to bring over" (welcome form) → the transfer steps
  // (listings/escrow/bba) drop out of the wizard entirely. Safe to change
  // mid-session: the flag is set at the welcome step, before any affected step
  // is reachable.
  const wizSteps = useMemo(
    () => gate?.skipTransfers ? STEPS.filter(s => !["listings", "escrow", "bba"].includes(s.k)) : STEPS,
    [gate?.skipTransfers]
  );
  const stepIdx = (k) => wizSteps.findIndex(s => s.k === k);

  // ---- Server-backed resources (lazy: only fetched once that step is reached) ----
  // Listings come from the brokerage ORG grant, pinned to the agent's long MlsId
  // (from /v1/me — the agent NEVER types it). AUTOLOAD: the moment the agent reaches
  // the Listings step (furthest >= 3) this `enabled` flips true and useResource
  // fetches automatically (cache-first) — zero clicks. "Refresh" is only a manual
  // secondary re-pull (live=1). No mlsId yet → no fetch (demo fallback).
  const byAgentPath = agent.mlsId
    ? `/v1/listings/by-agent?mlsId=${encodeURIComponent(agent.mlsId)}`
    : "/v1/listings/by-agent";
  // Indices are dynamic: the transfer steps may be absent (skipTransfers), so
  // resolve them from the live step list. Absent step (idx -1) → never fetch.
  const listingsRes = useResource(byAgentPath, { enabled: authed && !!agent.mlsId && stepIdx("listings") !== -1 && furthest >= stepIdx("listings"), pick: (r) => r });
  const escrowsRes  = useResource("/v1/escrows/mine",    { enabled: authed && stepIdx("escrow") !== -1 && furthest >= stepIdx("escrow"), pick: (r) => r.escrows || [] });
  const bbasRes     = useResource("/v1/bbas",            { enabled: authed && stepIdx("bba") !== -1 && furthest >= stepIdx("bba"), pick: (r) => r.bbas || [] });

  // Mirror fetched data into local state so steps can mutate transferStatus
  // optimistically after a "send for signature".
  // Starts EMPTY, not with fixtures. The effect below resolves the real value,
  // but seeding mock data meant any render before it — or any path where it does
  // not run — showed four demo properties as though they were the agent's.
  const [listings, setListings] = useState([]);
  const [listingProvenance, setListingProvenance] = useState(null);
  const [escrows, setEscrows] = useState([]);
  const [bbas, setBbas] = useState([]);

  // Listings provenance (3 distinct states — never silent sample data on failure):
  //   • live  — feed is active (connected:true): render real results, incl. 0.
  //   • demo  — no active feed (connected:false) OR agent has no MlsId yet: the
  //             ONLY genuine fallback to sample listings.
  //   • error — the pull failed: clear listings and let the step show a retry,
  //             NOT sample data (a silent sample hid the un-wired feed before).
  useEffect(() => {
    if (!authed) return;
    // A RECRUIT has no MLS id — that is the normal case, not a demo. Falling back
    // to MOCK_LISTINGS here put four fixture properties into their wizard, their
    // completion summary AND the staff email, which reported "4 listing transfers
    // pending" for an agent who has no listings at all. Demo data must never
    // reach a real agent's record.
    if (!agent.mlsId) {
      setListings(agent.recruit ? [] : MOCK_LISTINGS);
      setListingProvenance({ mode: agent.recruit ? "none" : "demo" });
      return;
    }
    const d = listingsRes.data;
    if (listingsRes.error) {
      setListings([]);
      setListingProvenance({ mode: "error" });
    } else if (d && d.connected) {
      setListings(d.listings || []);           // count 0 is a legitimate empty state
      setListingProvenance({ mode: "live", count: d.count, feed: d.matchedFeed, mlsId: agent.mlsId });
    } else if (d) {
      // connected:false. For a demo that is the intended sample-data fallback,
      // but for a REAL recruit it is the same leak as the no-mls-id path above:
      // fixtures would enter their checklist and the staff email as outstanding
      // transfers. An un-connected feed is "we don't know", not "here are four
      // properties".
      setListings(agent.recruit ? [] : MOCK_LISTINGS);
      setListingProvenance({ mode: agent.recruit ? "unavailable" : "demo" });
    }
  }, [authed, agent.mlsId, listingsRes.data, listingsRes.error]);
  useEffect(() => { if (escrowsRes.data) setEscrows(escrowsRes.data); }, [escrowsRes.data]);
  useEffect(() => { if (bbasRes.data) setBbas(bbasRes.data); }, [bbasRes.data]);

  const [signDoc, setSignDoc] = useState(null); // {doc, previewOnly, onSignedCb}

  // A preview writes NOTHING here. These keys are per-browser, so a preview would
  // otherwise leave one agent's position behind for the next agent previewed —
  // and the whole point is to show where THIS agent is.
  useEffect(() => { if (!previewing) localStorage.setItem("ep_onb_step", String(step)); }, [step, previewing]);
  useEffect(() => { if (!previewing) localStorage.setItem("ep_onb_furthest", String(furthest)); }, [furthest, previewing]);
  useEffect(() => { localStorage.setItem("ep_onb_tweaks", JSON.stringify(tweaks)); }, [tweaks]);
  useEffect(() => { window.__listings = listings; }, [listings]);
  // Once authed, never sit on the search step.
  useEffect(() => { if (authed && step < 1) setStep(1); }, [authed]); // eslint-disable-line react-hooks/exhaustive-deps

  // RESUME — put whoever is looking where the SERVER says the agent is.
  //
  // The wizard's position lives in localStorage, which is per-BROWSER. That is
  // fine while the agent stays in one, and wrong the moment they do not: opening
  // the invite on a phone after starting on a laptop, or clearing site data, put
  // them back on Your Profile with every later step locked, however much they had
  // actually finished. It was also why the Agent Services preview opened wherever
  // the previewer last was, and why previewing a second agent inherited the
  // first one's position.
  //
  // TWO RULES, because the two cases want different things:
  //
  //   PREVIEW     always place, and set furthest EXACTLY. Agent Services is
  //               looking at somebody else's state and should see that agent's
  //               locks reproduced, not a superset of them.
  //
  //   REAL AGENT  place only when this browser has no position of its own, so
  //               somebody halfway through the welcome form is never yanked
  //               elsewhere by a gate poll landing. `furthest` only ever RISES —
  //               if the server knows more than this browser does (an ICA signed
  //               from the BoldSign email rather than in-app, say) that unlocks
  //               the next step, and nothing here can re-lock one.
  //
  // Once per load either way. After that they navigate freely, including back to
  // re-read a step, and nothing repositions them.
  //
  // `previewing` is reliable by the time this runs: the gate is only fetched once
  // `authed` is true, and authed means /v1/me has already answered.
  const placed = useRef(false);
  useEffect(() => {
    if (!gate || placed.current) return;
    const i = stepIdx(resumeStepKey(gate));
    if (i < 0) return;
    placed.current = true;

    if (previewing) { setStep(i); setFurthest(i); return; }

    if (!hadStoredStep) setStep(i);
    setFurthest((f) => Math.max(f, i));
  }, [previewing, gate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hydrate signed agreements from the SERVER, once per session.
  //
  // `agreements` is written only by the signing modal, so a page reload lost it
  // and a recruit who had genuinely signed was stranded on the Agreements step
  // with "skip" as the only way out — while signed_documents said 'completed'.
  // See GET /v1/documents/mine for the full account.
  //
  // Waits on `agent` because which doc_type backs "everyplace" depends on
  // recruit status: recruits sign 'ica', transfer-day agents 'everyplace_package'.
  const [serverDocs, setServerDocs] = useState(null);
  useEffect(() => {
    if (!authed || !agent) return;
    let cancelled = false;
    api.get("/v1/documents/mine")
      .then(({ docs }) => {
        if (cancelled) return;
        setServerDocs(docs || {});
        const epType = agent.recruit ? "ica" : "everyplace_package";
        const fmt = (iso) => new Date(iso).toLocaleDateString("en-US",
          { month: "short", day: "numeric", year: "numeric" });
        setAgreements((curr) => {
          const next = { ...curr };
          const put = (key, d) => {
            // Never downgrade what this session already saw — the modal is more
            // current than the server between signing and the webhook landing.
            if (d?.signed && !next[key]?.signed) next[key] = { signed: true, when: fmt(d.updatedAt) };
          };
          put("everyplace", docs?.[epType]);
          put("laughton", docs?.laughton_amendments);
          // CRITICAL: set `package` in this SAME update when both are present.
          // The effect below fires onPackageSigned (Notion tracker creation)
          // guarded only by !agreements.package, which is session state — so
          // hydrating both without this would re-create the Notion page on every
          // reload. Batched together, the guard is already false when it runs.
          if (next.everyplace?.signed && next.laughton?.signed && !next.package) {
            next.package = { signed: true, when: next.laughton.when || next.everyplace.when,
                             foundingAgent: isFoundingAgent };
          }
          return next;
        });
      })
      .catch(() => { /* fail open — the step still works from session state */ });
    return () => { cancelled = true; };
    // Re-runs when the polled gate flips icaDone. Without that dependency this
    // fetched exactly once, at mount — so a session that loaded BEFORE the
    // completion webhook landed kept `agreements.everyplace` empty for its whole
    // life. The Agreements step still unlocked (it reads gate.icaDone directly),
    // but every consumer of `agreements` stayed wrong, and Finish reported a
    // signed ICA as "not yet signed" into the recap email, the Notion status and
    // the milestones notes. Refetching on the flip repairs all of them at once,
    // and `put` refuses to downgrade, so re-running is safe.
  }, [authed, agent?.id, agent?.recruit, gate?.icaDone]); // eslint-disable-line react-hooks/exhaustive-deps

  // When both individual agreements are signed, write agreements.package and fire
  // the Notion tracker creation. Guarded by !agreements.package so it only runs once.
  useEffect(() => {
    if (agreements.everyplace?.signed && agreements.laughton?.signed && !agreements.package) {
      const when = agreements.laughton?.when || agreements.everyplace?.when || new Date().toISOString();
      setAgreements(c => ({ ...c, package: { signed: true, when, foundingAgent: isFoundingAgent } }));
      onPackageSigned(when);
    }
  }, [agreements.everyplace?.signed, agreements.laughton?.signed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Edit-mode protocol (dev/demo affordance — preserved from the prototype)
  useEffect(() => {
    const handler = (ev) => {
      if (ev.data?.type === "__activate_edit_mode") setTweaksOn(true);
      if (ev.data?.type === "__deactivate_edit_mode") setTweaksOn(false);
    };
    window.addEventListener("message", handler);
    window.parent.postMessage({ type: "__edit_mode_available" }, "*");
    return () => window.removeEventListener("message", handler);
  }, []);

  function setTweak(k, v) {
    const edits = { [k]: v };
    setTweaks((curr) => ({ ...curr, ...edits }));
    window.parent.postMessage({ type: "__edit_mode_set_keys", edits }, "*");
  }

  // Called when the agreement package is signed. Creates the Notion tracker row.
  async function onPackageSigned(when) {
    try {
      const res = await api.post("/v1/onboarding-tracker", {
        license:       agent.license,
        foundingAgent: isFoundingAgent,
      });
      if (res?.notionId) setNotionId(res.notionId);
    } catch (e) {
      console.warn("[tracker] create failed:", e.message);
    }
  }

  // Pushes a checkpoint update to the server, which records it in
  // onboarding_milestones and mirrors to Notion when that's configured.
  // Fire-and-forget — never blocks the wizard flow.
  //
  // The PATCH is now UNCONDITIONAL. It used to bail out when there was no Notion
  // page id (`if (!id) return`), so with Notion unconfigured every milestone —
  // ADRE confirmation included — was dropped before it ever left the browser.
  // notionId is passed along when known, purely so the server can mirror.
  async function updateTracker(updates) {
    let id = notionId;
    if (!id) {
      // Best-effort late-create so Notion (when on) has a row to mirror into.
      // A failure here no longer skips the update: the server persists without it.
      try {
        const res = await api.post("/v1/onboarding-tracker", {
          license:       agent.license,
          foundingAgent: isFoundingAgent,
        });
        if (res?.notionId) { id = res.notionId; setNotionId(res.notionId); }
      } catch (e) {
        console.warn("[tracker] late-create failed (continuing):", e.message);
      }
    }
    try { await api.patch("/v1/onboarding-tracker", { ...(id ? { notionId: id } : {}), updates }); }
    catch (e) { console.warn("[tracker] update failed:", e.message); }
  }

  // "Start over" — clear BOTH layers: server verified session (clears ep_session)
  // and local onboarding progress. Full reload lands on a fresh Find You search
  // (no resumed agent). Explicit only; no auto-reset.
  async function startOver() {
    if (!window.confirm("Start over? This signs you out and clears your onboarding progress on this device.")) return;
    try { await api.post("/v1/onboard/signout"); } catch { /* clear locally regardless */ }
    localStorage.removeItem("ep_onb_step");
    localStorage.removeItem("ep_onb_furthest");
    window.location.href = "/";
  }

  // Onboarding gate (new-recruit pipeline): the ICA + Welcome form must both be
  // complete before the transfer steps (ADRE onward) unlock. Server-owned state
  // — /v1/onboarding-gate/status returns unlocked:true for pre-pipeline
  // (transfer-day) agents with no intake row, so their flow is unchanged.
  // (gate state + wizSteps are declared earlier, above the resource hooks.)
  const GATE_LOCK_FROM = stepIdx("adre"); // first locked step
  const gateLocked = !!(gate?.tracked && !gate.unlocked);

  const goTo = (i) => {
    if (gateLocked && i >= GATE_LOCK_FROM) return; // transfer steps locked until ICA + Welcome complete
    setStep(i); if (i > furthest) setFurthest(i);
  };
  const next = () => goTo(Math.min(effectiveStep + 1, wizSteps.length - 1));
  const back = () => goTo(Math.max(effectiveStep - 1, 0));

  function openSign(doc, previewOnly, onSignedCb) {
    setSignDoc({ doc, previewOnly: !!previewOnly, onSignedCb });
  }
  function handleSigned(when) {
    const { doc, onSignedCb } = signDoc;
    if (doc.k && !doc.k.startsWith("transfer_") && !doc.k.endsWith("_view")) {
      setAgreements((curr) => ({
        ...curr,
        [doc.k]: { signed: true, when, required: true, foundingAgent: doc.foundingAgent },
      }));
    }
    onSignedCb && onSignedCb(when);
    // ICA completion opens the onboarding gate server-side (via the BoldSign
    // webhook), which can take minutes — poll until it lands rather than
    // checking once and giving up.
    pollGateUntilSigned();
    setTimeout(() => setSignDoc(null), 1000);
  }

  // Brief flash-guard while the initial /v1/me check runs.
  if (me.loading && !me.data && !me.error) {
    return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--ep-saguaro-blossom)", fontFamily: "var(--ep-font-body)", color: "var(--ep-fg-muted)" }}>Loading…</div>;
  }

  const currentStep = wizSteps[effectiveStep];

  // ADRE next: set adreConfirmed then advance
  function adreNext() { setAdreConfirmed(true); next(); }

    // Steps hidden from the wizard right now — keeps the "Step NN / MM" eyebrows
  // honest instead of jumping 05 → 09 with nothing in between.
  const hiddenNumbered = gate?.skipTransfers ? ["listings", "escrow", "bba"] : [];

const stepContent = {
    search:       <StepSearch/>,
    intro:        <StepIntro agent={agent} onNext={next}/>,
    profile:      <StepProfile agent={agent} setAgent={updated => setAgentEdits(e => ({...e, email: updated.email, phone: updated.phone}))} onNext={next} onBack={back}/>,
    agreements:   <StepAgreements eyebrow={stepEyebrow("agreements", hiddenNumbered)} agreements={agreements} agent={agent} gate={gate} serverDocs={serverDocs}
                    onAwaitingSignature={pollGateUntilSigned} onNext={next} onBack={back}
                    onEveryplaceSigned={(when) => setAgreements(c => ({...c, everyplace: {signed:true, when}}))}
                    onLaughtonSigned={(when) => setAgreements(c => ({...c, laughton: {signed:true, when}}))}/>,
    welcome:      <StepWelcomeForm agent={agent} onNext={next} onBack={back} gate={gate} refreshGate={refreshGate}/>,
    adre:         <StepADRE eyebrow={stepEyebrow("adre", hiddenNumbered)} agent={agent} gate={gate} onNext={adreNext} onBack={back} updateTracker={updateTracker}/>,
    associations: <StepAssociations eyebrow={stepEyebrow("associations", hiddenNumbered)} associations={associations} setAssociations={setAssociations} agent={agent} openSign={openSign} onNext={next} onBack={back} updateTracker={updateTracker}/>,
    listings:     <StepListings listings={listings} setListings={setListings} agent={agent} openSign={openSign} onNext={next} onBack={back}
                    associations={associations} loading={listingsRes.loading && !!agent.mlsId} provenance={listingProvenance} onReload={() => listingsRes.reload({ live: true })}/>,
    escrow:       <StepEscrow escrows={escrows} setEscrows={setEscrows} agent={agent} openSign={openSign} onNext={next} onBack={back}
                    loading={escrowsRes.loading} error={escrowsRes.error} onReload={() => escrowsRes.reload({ live: true })}/>,
    bba:          <StepBBA bbas={bbas} setBbas={setBbas} agent={agent} openSign={openSign} onNext={next} onBack={back}
                    loading={bbasRes.loading} error={bbasRes.error} onReload={() => bbasRes.reload({ live: true })}/>,
    finish:       <StepFinish eyebrow={stepEyebrow("finish", hiddenNumbered)} agent={agent} agreements={agreements} gate={gate} serverDocs={serverDocs}
                    associations={associations} listings={listings} escrows={escrows} bbas={bbas}
                    adreConfirmed={adreConfirmed} notionId={notionId} updateTracker={updateTracker} onNext={next}/>,
    circle:       <StepCircle agent={agent} gate={gate} onBack={back}/>,
  }[currentStep.k];

  return (
    <div data-screen-label={`0${effectiveStep + 1} ${currentStep.label}`}>
      {tweaks.layout === "wizard" ? (
        <WizardLayout step={effectiveStep} furthest={furthest} onStep={goTo} agent={agent} authed={authed} onStartOver={startOver} steps={wizSteps}>{stepContent}</WizardLayout>
      ) : (
        <DashboardLayout step={effectiveStep} furthest={furthest} onStep={goTo} agent={agent} authed={authed} onStartOver={startOver} agreements={agreements} listings={listings} escrows={escrows} bbas={bbas} steps={wizSteps}>{stepContent}</DashboardLayout>
      )}

      {signDoc && (
        <SignatureModal doc={signDoc.doc} previewOnly={signDoc.previewOnly}
          signatureStyle={tweaks.signatureStyle} agent={agent}
          onClose={() => setSignDoc(null)}
          onSigned={handleSigned}/>
      )}

      {tweaksOn && (
        <div className="tweaks-panel">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div className="ep-eyebrow" style={{ color: "var(--ep-high-noon)", margin: 0 }}>Tweaks</div>
            <button onClick={() => setTweaksOn(false)} style={{ background: "transparent", border: 0, color: "var(--ep-saguaro-blossom)", cursor: "pointer" }}><IcX size={14}/></button>
          </div>
          <div className="tw-row">
            <label style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.7 }}>Layout</label>
            <select value={tweaks.layout} onChange={(e) => setTweak("layout", e.target.value)}>
              <option value="wizard">Wizard — sidebar nav</option>
              <option value="dashboard">Dashboard — tiles overview</option>
            </select>
          </div>
          <div className="tw-row">
            <label style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.7 }}>Signature Modal</label>
            <select value={tweaks.signatureStyle} onChange={(e) => setTweak("signatureStyle", e.target.value)}>
              <option value="docusign">DocuSign style (yellow tabs)</option>
              <option value="minimal">Minimal EveryPlace</option>
              <option value="wet">Wet signature (draw)</option>
            </select>
          </div>
          <div style={{ fontSize: 11, opacity: 0.6, marginTop: 10, lineHeight: 1.4 }}>
            Click through the onboarding to see tweaks applied. Progress is saved.
          </div>
          <button className="ep-btn ghost sm" style={{ color: "var(--ep-saguaro-blossom)", marginTop: 10, padding: "8px 12px", fontSize: 10 }}
            onClick={() => { localStorage.removeItem("ep_onb_step"); localStorage.removeItem("ep_onb_furthest"); location.reload(); }}>
            Reset progress
          </button>
        </div>
      )}
    </div>
  );
}

// Height of the read-only preview banner (ReadOnlyBanner, below). Declared here
// because WizardLayout offsets the sticky sidebar by exactly this much — the two
// numbers have to move together or the banner covers the wordmark on scroll.
const PREVIEW_BANNER_H = 44;

// ============ WIZARD LAYOUT ============
function WizardLayout({ step, furthest, onStep, agent, authed, onStartOver, steps = STEPS, children }) {
  const pct = ((step) / (steps.length - 1)) * 100;
  const isMobile = useIsMobile();

  // Phones get the native app-shell (top app-bar + progress + menu sheet); desktop
  // keeps the sidebar layout below, unchanged.
  if (isMobile) {
    return (
      <MobileWizardShell step={step} furthest={furthest} onStep={onStep} agent={agent}
        authed={authed} onStartOver={onStartOver} pct={pct} steps={steps}>{children}</MobileWizardShell>
    );
  }

  // The read-only banner is sticky, and so is the sidebar. Without an offset they
  // pin to the same line and the banner covers the EveryPlace wordmark as soon as
  // anyone scrolls. Nudge the sidebar down by exactly the banner's height, and only
  // in a preview — a real agent's layout is untouched.
  const previewing = !!agent?.viewAs?.readOnly;
  const bannerH = previewing ? PREVIEW_BANNER_H : 0;

  return (
    <>
    <ReadOnlyBanner agent={agent}/>
    <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", minHeight: "100vh" }}>
      {/* Sidebar */}
      <aside style={{ background: "var(--ep-origin)", color: "var(--ep-saguaro-blossom)", padding: "28px 0 28px", position: "sticky", top: bannerH, height: `calc(100vh - ${bannerH}px)`, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "0 28px 28px", borderBottom: "1px solid rgba(255,245,228,0.12)" }}>
          <EPLogo height={22}/>
          <div className="ep-caption" style={{ color: "rgba(255,245,228,0.6)", marginTop: 12 }}>Agent Onboarding</div>
        </div>

        <nav style={{ padding: "16px 0", flex: 1 }}>
          {steps.map((s, i) => {
            // Steps stay locked until the agent has verified (authed) and reached them.
            const locked = !authed ? i > 0 : i > furthest;
            const done = i < step;
            const active = i === step;
            return (
              <div key={s.k}
                className={`wiz-step ${active ? "active" : ""} ${done ? "done" : ""} ${locked ? "locked" : ""}`}
                onClick={() => !locked && onStep(i)}>
                <div className="wiz-dot">{done ? <IcCheck size={13}/> : (locked ? <IcLock size={11}/> : String(i + 1).padStart(2, "0"))}</div>
                <div>
                  <div style={{ font: "500 14px/1.2 var(--ep-font-body)" }}>{s.label}</div>
                  <div style={{ font: "400 11px/1 var(--ep-font-body)", opacity: 0.6, marginTop: 3, letterSpacing: "0.04em" }}>
                    {active ? "In progress" : done ? "Complete" : locked ? "Locked" : "Ready"}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        <div style={{ padding: "20px 28px", borderTop: "1px solid rgba(255,245,228,0.12)" }}>
          <div className="ep-caption" style={{ color: "rgba(255,245,228,0.6)", marginBottom: 8 }}>{authed ? "Signed in as" : "Not signed in"}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 999, background: "var(--ep-high-noon)", color: "var(--ep-origin)", display: "flex", alignItems: "center", justifyContent: "center", font: "600 13px/1 var(--ep-font-body)" }}>
              {agent.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{agent.name}</div>
              <div style={{ fontSize: 11, opacity: 0.6 }}>{agent.license || (authed ? "" : "Verify to begin")}</div>
            </div>
          </div>
          {authed && (
            <button onClick={onStartOver}
              style={{ marginTop: 14, background: "transparent", border: "1px solid rgba(255,245,228,0.2)", color: "rgba(255,245,228,0.75)", borderRadius: 2, padding: "8px 12px", fontSize: 12, cursor: "pointer", width: "100%", fontFamily: "var(--ep-font-body)" }}>
              Not you? Start over
            </button>
          )}
        </div>
      </aside>

      {/* Main */}
      <main style={{ padding: "40px clamp(32px, 5vw, 80px) 80px", background: "var(--ep-saguaro-blossom)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
            <div style={{ flex: 1 }}>
              <div className="progress-bar"><span style={{ width: `${pct}%` }}/></div>
            </div>
            <div className="ep-caption" style={{ color: "var(--ep-fg-muted)" }}>Step {step + 1} of {steps.length}</div>
          </div>
          {children}
        </div>
      </main>
    </div>
    </>
  );
}

// ============ READ-ONLY PREVIEW BANNER ============
// Shown whenever Agent Services is previewing the app as an agent.
//
// Persistent and loud on purpose. The preview renders every screen exactly as the
// agent sees it — that is what makes it useful — which also means there is
// otherwise NOTHING on screen to say whose session this is. A screenshot taken
// from a preview should be self-labelling, and somebody who wanders back to the
// tab an hour later should not have to work it out.
//
// Enforcement is server-side (lib/auth.js blockReadOnlyWrites). This bar is a
// sign, not a lock.
//
// Its height (PREVIEW_BANNER_H, declared above WizardLayout) is shared with
// WizardLayout, which offsets the sticky sidebar by exactly this much so the two
// do not pin to the same line.
function ReadOnlyBanner({ agent }) {
  if (!agent?.viewAs?.readOnly) return null;
  return (
    <div style={{ background: "var(--ep-sunset)", color: "#FFF5E4", padding: "10px 20px", height: PREVIEW_BANNER_H, boxSizing: "border-box",
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
      flexWrap: "wrap", font: "500 13px/1.4 var(--ep-font-body)", position: "sticky", top: 0, zIndex: 1000 }}>
      <span>
        Read-only preview — you are seeing the app as <strong>{agent.name || agent.email}</strong>.
        Nothing can be signed or submitted.
      </span>
      <button className="ep-btn ghost sm"
        style={{ color: "#FFF5E4", borderColor: "rgba(255,245,228,0.5)" }}
        onClick={async () => {
          try { await api.post("/staff/exit-view-as", {}); } catch { /* leaving anyway */ }
          window.location.href = "/staff";
        }}>
        Exit preview
      </button>
    </div>
  );
}

// ============ MOBILE WIZARD SHELL (phones) ============
// Native app-shell: a dark top app-bar (logo + step-dots), a slim progress strip,
// full-width scrolling content, and a slide-in menu sheet (the step list + Start
// over). Rendered only at the phone breakpoint — desktop is untouched.
function MobileWizardShell({ step, furthest, onStep, agent, authed, onStartOver, pct, steps = STEPS, children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const initials = agent.name.split(" ").map((w) => w[0]).slice(0, 2).join("");

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "var(--ep-saguaro-blossom)" }}>
      {/* Top app-bar (sticky, dark, safe-area aware) */}
      <header style={{ position: "sticky", top: 0, zIndex: 30, background: "var(--ep-origin)", color: "var(--ep-saguaro-blossom)", paddingTop: "var(--safe-top)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px" }}>
          <button onClick={() => setMenuOpen(true)} aria-label="Menu"
            style={{ background: "transparent", border: 0, color: "inherit", padding: 4, display: "flex", cursor: "pointer" }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          <EPLogo height={18}/>
          {/* step dots */}
          <div style={{ marginLeft: "auto", display: "flex", gap: 5 }}>
            {steps.map((s, i) => (
              <span key={s.k} style={{ width: 7, height: 7, borderRadius: 999,
                background: i <= step ? "var(--ep-high-noon)" : "rgba(255,245,228,0.28)" }}/>
            ))}
          </div>
        </div>
        {/* progress strip */}
        <div style={{ padding: "0 16px 10px" }}>
          <div className="progress-bar"><span style={{ width: `${pct}%` }}/></div>
          <div style={{ fontSize: 12, opacity: 0.82, marginTop: 6 }}>
            Step {step + 1} of {steps.length} · {steps[step].label}
          </div>
        </div>
      </header>

      {/* Content (overflow-x guard so nothing can shove the page sideways; kept off
          <body> to preserve the sticky app-bar). minWidth:0 lets children shrink. */}
      <main style={{ flex: 1, minWidth: 0, overflowX: "hidden", padding: "20px 16px calc(28px + var(--safe-bottom))" }}>
        {children}
      </main>

      {/* Menu sheet (step list + Start over) */}
      {menuOpen && (
        <>
          <div onClick={() => setMenuOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 40 }}/>
          <aside style={{ position: "fixed", top: 0, left: 0, bottom: 0, width: "min(310px, 84vw)", zIndex: 50,
            background: "var(--ep-origin)", color: "var(--ep-saguaro-blossom)", display: "flex", flexDirection: "column",
            paddingTop: "var(--safe-top)", boxShadow: "2px 0 24px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 20px 16px", borderBottom: "1px solid rgba(255,245,228,0.12)" }}>
              <EPLogo height={20}/>
              <button onClick={() => setMenuOpen(false)} aria-label="Close"
                style={{ background: "transparent", border: 0, color: "inherit", cursor: "pointer", padding: 4, display: "flex" }}>
                <IcX size={16}/>
              </button>
            </div>
            <nav style={{ padding: "10px 0", flex: 1, overflowY: "auto" }}>
              {steps.map((s, i) => {
                const locked = !authed ? i > 0 : i > furthest;
                const done = i < step;
                const active = i === step;
                return (
                  <div key={s.k}
                    className={`wiz-step ${active ? "active" : ""} ${done ? "done" : ""} ${locked ? "locked" : ""}`}
                    onClick={() => { if (!locked) { onStep(i); setMenuOpen(false); } }}>
                    <div className="wiz-dot">{done ? <IcCheck size={13}/> : (locked ? <IcLock size={11}/> : String(i + 1).padStart(2, "0"))}</div>
                    <div>
                      <div style={{ font: "500 14px/1.2 var(--ep-font-body)" }}>{s.label}</div>
                      <div style={{ font: "400 11px/1 var(--ep-font-body)", opacity: 0.6, marginTop: 3, letterSpacing: "0.04em" }}>
                        {active ? "In progress" : done ? "Complete" : locked ? "Locked" : "Ready"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </nav>
            <div style={{ padding: "16px 20px calc(20px + var(--safe-bottom))", borderTop: "1px solid rgba(255,245,228,0.12)" }}>
              <div className="ep-caption" style={{ color: "rgba(255,245,228,0.6)", marginBottom: 8 }}>{authed ? "Signed in as" : "Not signed in"}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 999, background: "var(--ep-high-noon)", color: "var(--ep-origin)", display: "flex", alignItems: "center", justifyContent: "center", font: "600 13px/1 var(--ep-font-body)" }}>{initials}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{agent.name}</div>
                  <div style={{ fontSize: 11, opacity: 0.6 }}>{agent.license || (authed ? "" : "Verify to begin")}</div>
                </div>
              </div>
              {authed && (
                <button onClick={onStartOver}
                  style={{ marginTop: 14, background: "transparent", border: "1px solid rgba(255,245,228,0.2)", color: "rgba(255,245,228,0.75)", borderRadius: 2, padding: "10px 12px", fontSize: 13, cursor: "pointer", width: "100%", fontFamily: "var(--ep-font-body)" }}>
                  Not you? Start over
                </button>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}

// ============ DASHBOARD LAYOUT ============
function DashboardLayout({ step, furthest, onStep, agent, authed, onStartOver, agreements, listings, escrows, bbas, steps = STEPS, children }) {
  const agreePackage     = !!agreements.everyplace?.signed && !!agreements.laughton?.signed;
  const transferredCount = listings.filter((l) => l.transferStatus === "transferred").length;
  const escrowDoneCount  = (escrows || []).filter((e) => e.transferStatus === "transferred").length;
  const bbaList          = bbas || [];
  const bbaSelected      = bbaList.filter((b) => b.selected);
  const bbaDoneCount     = bbaList.filter((b) => b.transferStatus === "transferred").length;

  // Tile definitions keyed by step; index/locked/status derive from the LIVE
  // step list (skipped steps simply don't render a tile).
  const tileDefs = {
    profile:      { icon: <IcUser size={22}/>,      title: "Your Profile",          sub: "License and contact info" },
    agreements:   { icon: <IcDoc size={22}/>,       title: "Agreements",            sub: agreePackage ? "Package signed" : "Sign agreement package", done: agreePackage },
    welcome:      { icon: <IcDoc size={22}/>,       title: "Welcome Form",          sub: "Your team profile details" },
    adre:         { icon: <IcShield size={22}/>,    title: "ADRE Transfer",         sub: "Change of employing broker" },
    associations: { icon: <IcBuilding size={22}/>,  title: "Association Transfers", sub: "REALTOR membership change forms" },
    listings:     { icon: <IcBuilding size={22}/>,  title: "Listing Transfers",     sub: `${transferredCount} of ${listings.length} transferred`,
                    done: transferredCount === listings.length && !!listings.length },
    escrow:       { icon: <IcEscrow size={22}/>,    title: "Escrow Transfers",      sub: `${escrowDoneCount} of ${(escrows || []).length} transferred`,
                    done: escrowDoneCount === (escrows || []).length && !!(escrows || []).length },
    bba:          { icon: <IcHandshake size={22}/>, title: "Buyer Agreements",      sub: bbaSelected.length ? `${bbaDoneCount} of ${bbaSelected.length} transferred` : "From Followup Boss",
                    done: !!bbaSelected.length && bbaDoneCount === bbaSelected.length },
    finish:       { icon: <IcCheck size={22}/>,     title: "Finish",                sub: "Summary and recap email", neverDone: true },
    circle:       { icon: <IcExternal size={22}/>,  title: "Circle Training",       sub: "Your training & onboarding hub", neverDone: true },
  };
  const tiles = steps
    .map((s, i) => ({ k: s.k, i, def: tileDefs[s.k] }))
    .filter((t) => t.k !== "search" && t.def)
    .map(({ k, i, def }) => {
      const done   = def.neverDone ? false : (def.done !== undefined ? def.done : furthest > i);
      const locked = furthest < i;
      return { k, i, icon: def.icon, title: def.title, sub: def.sub, done, locked,
        status: done ? "Complete" : locked ? "Locked" : (step === i ? "In progress" : "Ready") };
    });

  return (
    <div style={{ minHeight: "100vh", background: "var(--ep-saguaro-blossom)" }}>
      <header className="topbar">
        <div className="hstack" style={{ gap: 20 }}>
          <EPLogo height={20}/>
          <span style={{ opacity: 0.3 }}>/</span>
          <span className="ep-caption" style={{ color: "rgba(255,245,228,0.75)" }}>Agent Onboarding</span>
        </div>
        <div className="hstack" style={{ gap: 16 }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ep-saguaro-blossom)" }}>{agent.name}</div>
            <div style={{ fontSize: 11, opacity: 0.65 }}>{agent.license}</div>
          </div>
          <div style={{ width: 34, height: 34, borderRadius: 999, background: "var(--ep-high-noon)", color: "var(--ep-origin)", display: "flex", alignItems: "center", justifyContent: "center", font: "600 12px/1 var(--ep-font-body)" }}>
            {agent.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
          </div>
          {authed && (
            <button onClick={onStartOver} className="ep-btn ghost sm" style={{ color: "var(--ep-saguaro-blossom)" }}>
              Not you? Start over
            </button>
          )}
        </div>
      </header>

      <div style={{ padding: "40px clamp(24px, 4vw, 64px) 80px", maxWidth: 1280, margin: "0 auto" }}>
        {step === 0 ? children : (
          <>
            <div style={{ background: "var(--ep-origin)", color: "var(--ep-saguaro-blossom)", padding: 32, marginBottom: 32, borderRadius: 2, display: "grid", gridTemplateColumns: "1.3fr auto", gap: 32, alignItems: "center" }}>
              <div>
                <div className="ep-eyebrow" style={{ color: "var(--ep-high-noon)" }}>Your Onboarding</div>
                <h2 style={{ font: "300 36px/1.1 var(--ep-font-display)", margin: "8px 0 6px", color: "var(--ep-saguaro-blossom)" }}>{agent.name.split(" ")[0]}, you're {Math.round(((furthest) / (steps.length - 1)) * 100)}% there.</h2>
                <div style={{ color: "rgba(255,245,228,0.75)", fontSize: 14 }}>Complete the remaining steps at your own pace. Save anytime.</div>
              </div>
              <button className="ep-btn primary" onClick={() => onStep(Math.max(step, 1))}>Continue <IcArrow/></button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 48 }}>
              {tiles.map((t) => (
                <div key={t.k}
                  className={`dash-tile ${t.locked ? "locked" : ""}`}
                  onClick={() => !t.locked && onStep(t.i)}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ width: 40, height: 40, borderRadius: 2, background: t.done ? "var(--ep-avocado)" : "var(--ep-origin-10)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ep-origin)" }}>
                      {t.done ? <IcCheck size={20}/> : t.icon}
                    </div>
                    <div className="ep-caption" style={{ color: t.done ? "var(--ep-down-to-earth)" : "var(--ep-fg-muted)" }}>{String(t.i).padStart(2, "0")}</div>
                  </div>
                  <div style={{ font: "300 22px/1.15 var(--ep-font-display)", letterSpacing: "-0.01em" }}>{t.title}</div>
                  <div style={{ fontSize: 13, color: "var(--ep-fg-muted)", flex: 1 }}>{t.sub}</div>
                  <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600, color: t.locked ? "var(--ep-fg-subtle)" : (t.done ? "var(--ep-down-to-earth)" : "var(--ep-sunset)") }}>
                    {t.locked ? <><IcLock size={10}/> {t.status}</> : t.status}
                  </div>
                </div>
              ))}
            </div>

            {children}
          </>
        )}
      </div>
    </div>
  );
}
